import { useState } from 'react';
import { MEET_LINK_MAX, normaliseerMeetLink } from '@tolk/shared';
import { ApiFout, wijzigKamer } from '../lib/api';
import MeetKnop from './MeetKnop';

/**
 * Het Meet-blok bovenaan de zijbalk.
 *
 * Taak 1 van 23 september 2026: vanaf de linkerkant van het scherm een Meet
 * STARTEN of OPENEN, zonder te scrollen. Openen kan iedereen; starten en de
 * link bewaren alleen de maker (`beheer`), want de link komt bij de kamer te
 * staan en de gast ziet hem.
 *
 * DE GROTE KNOP ALLEEN OP EEN BREED SCHERM. Op een telefoon staat de kleine
 * Meet-knop al bovenaan in het gesprek, en dit blok belandt daar onderaan -
 * twee keer dezelfde knop is er een te veel.
 */
export default function MeetBlok({
  kamerId,
  meetLink,
  beheer,
}: {
  kamerId: string;
  meetLink: string | null;
  beheer: boolean;
}) {
  if (!beheer && meetLink === null) return null;

  return (
    // Een gast heeft op een telefoon niets in dit blok: de knop staat daar al
    // bovenaan. Dan ook geen losse kop onderaan.
    <section
      className={`mt-1.5 border-t border-slate-150 pt-2 space-y-2 lg:mt-0 lg:border-t-0 lg:pt-0
                  ${beheer ? '' : 'hidden lg:block'}`}
    >
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-450">Videogesprek</p>

      {meetLink !== null && (
        <div className="hidden lg:block">
          <MeetKnop link={meetLink} />
        </div>
      )}

      {beheer && meetLink === null && (
        <>
          {/*
            meet.google.com/new maakt meteen een nieuwe vergadering aan, in je
            eigen Google-account. Daarna kopieer je de link uit de adresbalk en
            plak je hem hieronder; pas dan ziet je gast hem. Automatisch kan
            niet: Meet geeft een nieuwe link niet terug aan een andere site.
          */}
          <a
            href="https://meet.google.com/new"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border-2
                       border-gio-blue text-gio-blue text-sm font-bold hover:bg-gio-blue
                       hover:text-white transition-colors duration-155"
          >
            Nieuwe Meet starten <span aria-hidden="true">↗</span>
          </a>
          <p className="text-[11px] text-slate-450 leading-relaxed">
            Kopieer daarna de link uit Meet en plak hem hieronder. Dan ziet je gast hem ook.
          </p>
        </>
      )}

      {beheer && <MeetInstellen kamerId={kamerId} meetLink={meetLink} />}
    </section>
  );
}

/**
 * De Meet-link zetten, wijzigen of weghalen, TIJDENS het gesprek. De gast
 * krijgt de knop meteen, zonder herladen: de server stuurt de nieuwe stand
 * over de kamerverbinding.
 */
function MeetInstellen({ kamerId, meetLink }: { kamerId: string; meetLink: string | null }) {
  const [open, setOpen] = useState(false);
  const [invoer, setInvoer] = useState(meetLink ?? '');
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  const ongeldig = invoer.trim() !== '' && normaliseerMeetLink(invoer) === null;

  async function bewaar(waarde: string | null): Promise<void> {
    setBezig(true);
    setFout(null);
    try {
      await wijzigKamer(kamerId, { meetLink: waarde });
      setOpen(false);
      if (waarde === null) setInvoer('');
    } catch (error) {
      setFout(error instanceof ApiFout ? error.message : 'Bewaren lukte niet.');
    } finally {
      setBezig(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setInvoer(meetLink ?? '');
          setOpen(true);
        }}
        className="text-[11px] font-bold text-gio-blue hover:text-gio-dark transition-colors duration-155"
      >
        {meetLink === null ? '+ Google Meet-link toevoegen' : 'Google Meet-link wijzigen'}
      </button>
    );
  }

  return (
    <div className="space-y-1.5">
      <input
        type="url"
        inputMode="url"
        value={invoer}
        maxLength={MEET_LINK_MAX}
        onChange={(e) => setInvoer(e.target.value)}
        placeholder="https://meet.google.com/abc-defg-hij"
        className="w-full p-2 bg-slate-50 border-2 border-slate-250 rounded-lg text-xs
                   text-slate-800 placeholder-slate-405 focus:outline-none focus:border-gio-blue
                   focus:bg-white transition-colors duration-155"
      />
      {ongeldig && (
        <p className="text-[11px] text-gio-danger">Alleen links van meet.google.com.</p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={bezig || ongeldig || invoer.trim() === ''}
          onClick={() => void bewaar(invoer)}
          className="flex-1 py-1.5 rounded-lg bg-gio-blue text-white text-[11px] font-bold
                     hover:bg-gio-dark transition-colors duration-155 disabled:opacity-50"
        >
          Bewaren
        </button>
        {meetLink !== null && (
          <button
            type="button"
            disabled={bezig}
            onClick={() => void bewaar(null)}
            className="px-3 py-1.5 rounded-lg border-2 border-slate-250 text-[11px] font-bold
                       text-slate-550 hover:text-gio-danger hover:border-gio-danger
                       transition-colors duration-155 disabled:opacity-50"
          >
            Weghalen
          </button>
        )}
        <button
          type="button"
          disabled={bezig}
          onClick={() => setOpen(false)}
          className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-slate-450
                     hover:text-slate-650 transition-colors duration-155"
        >
          Annuleren
        </button>
      </div>
      {fout !== null && <p className="text-[11px] text-gio-danger">{fout}</p>}
    </div>
  );
}

